import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Session } from '@supabase/supabase-js';

interface DeadlineItem {
  case_id: string;
  case_title: string;
  case_type: string;
  deadline: string;
  days_until_deadline: number;
  is_overdue: boolean;
  urgency: 'overdue' | 'critical' | 'high' | 'medium' | 'low';
}

interface DeadlineWidgetProps {
  session: Session;
  onNavigateToCase: (caseId: string) => void;
}

export const DeadlineWidget: React.FC<DeadlineWidgetProps> = ({ session, onNavigateToCase }) => {
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [daysAhead, setDaysAhead] = useState(30);

  useEffect(() => {
    fetchUpcomingDeadlines();
  }, [session.user.id, daysAhead]);

  const fetchUpcomingDeadlines = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .rpc('get_upcoming_deadlines', {
          user_uuid: session.user.id,
          days_ahead: daysAhead,
        });

      if (fetchError) throw fetchError;

      setDeadlines(data || []);
    } catch (err) {
      console.error('Error fetching deadlines:', err);
      setError(err instanceof Error ? err.message : 'Failed to load deadlines');
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'overdue':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700';
      case 'critical':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700';
      case 'high':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
      default:
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'overdue':
        return '🚨';
      case 'critical':
        return '⏰';
      case 'high':
        return '⚠️';
      case 'medium':
        return '📅';
      default:
        return '📌';
    }
  };

  const getUrgencyLabel = (urgency: string, daysUntil: number) => {
    if (urgency === 'overdue') {
      return Math.abs(daysUntil) === 1 ? '1 Tag überfällig' : `${Math.abs(daysUntil)} Tage überfällig`;
    }
    if (daysUntil === 0) return 'Heute fällig';
    if (daysUntil === 1) return 'Morgen fällig';
    return `In ${daysUntil} Tagen`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="material-symbols-outlined text-luna-secondary">schedule</span>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Anstehende Fristen</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luna-secondary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="material-symbols-outlined text-luna-secondary">schedule</span>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Anstehende Fristen</h2>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-luna-secondary">schedule</span>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Anstehende Fristen</h2>
        </div>
        
        {/* Filter */}
        <select
          value={daysAhead}
          onChange={(e) => setDaysAhead(Number(e.target.value))}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-luna-secondary focus:border-transparent"
        >
          <option value={7}>Nächste 7 Tage</option>
          <option value={14}>Nächste 14 Tage</option>
          <option value={30}>Nächste 30 Tage</option>
          <option value={60}>Nächste 60 Tage</option>
        </select>
      </div>

      {/* Deadlines List */}
      {deadlines.length === 0 ? (
        <div className="text-center py-8">
          <span className="material-symbols-outlined text-gray-400 dark:text-gray-500 text-5xl mb-3">check_circle</span>
          <p className="text-gray-600 dark:text-gray-400">Keine anstehenden Fristen</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Alle Ihre Fälle sind auf dem neuesten Stand
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {deadlines.map((deadline) => (
            <div
              key={deadline.case_id}
              onClick={() => onNavigateToCase(deadline.case_id)}
              className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md ${getUrgencyColor(deadline.urgency)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getUrgencyIcon(deadline.urgency)}</span>
                    <h3 className="font-semibold text-sm truncate">{deadline.case_title}</h3>
                  </div>
                  
                  <p className="text-xs opacity-80 mb-2">{deadline.case_type}</p>
                  
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-xs">event</span>
                      <span>{formatDate(deadline.deadline)}</span>
                    </div>
                    <div className="flex items-center gap-1 font-semibold">
                      <span className="material-symbols-outlined text-xs">hourglass_empty</span>
                      <span>{getUrgencyLabel(deadline.urgency, deadline.days_until_deadline)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateToCase(deadline.case_id);
                  }}
                  className="p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                  aria-label="Fall öffnen"
                >
                  <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Footer */}
      {deadlines.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              {deadlines.filter(d => d.is_overdue).length > 0 && (
                <span className="text-red-600 dark:text-red-400 font-semibold">
                  🚨 {deadlines.filter(d => d.is_overdue).length} überfällig
                </span>
              )}
              {deadlines.filter(d => d.urgency === 'critical' && !d.is_overdue).length > 0 && (
                <span className="text-orange-600 dark:text-orange-400 font-semibold">
                  ⏰ {deadlines.filter(d => d.urgency === 'critical' && !d.is_overdue).length} kritisch
                </span>
              )}
            </div>
            <span className="text-gray-500 dark:text-gray-400">
              Gesamt: {deadlines.length} {deadlines.length === 1 ? 'Frist' : 'Fristen'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
